/**
 * Task Enhanced Component
 * Unique Properties: Assignment matrix, priority queue, due dates, dependencies
 */
// @ts-nocheck


import { Button, Card, Container, Grid, Group, Modal, Paper, Progress, Select, Stack, Tabs, Text, TextInput, Title } from '@mantine/core';
import '@mantine/core/styles.css';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconChecklist, IconPlus, IconUsers } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { useTaskAssignments, useTaskStatusUpdates, useUserTaskUpdates } from '../hooks/useSubscriptions';
import { useTaskMutations as useMutation, useTasks } from '../hooks/useWorkflowState';

interface TaskProps {
    data?: any;
    mode?: 'kanban' | 'list' | 'calendar';
}

export const TaskEnhanced: React.FC<TaskProps> = ({ data = {}, mode = 'list' }) => {
    const [activeTab, setActiveTab] = useState('tasks');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newTaskData, setNewTaskData] = useState({
        title: '',
        priority: 'MEDIUM',
        assignee: '',
        description: '',
        due_date: ''
    });
    const tableRef = useRef<InstanceType<typeof TabulatorFull> | null>(null);
    const tableElementRef = useRef<HTMLDivElement>(null);

    // Use real data hooks
    const { tasks, loading, error, refetch } = useTasks();
    const { createTask, loading: creating } = useMutation<Record<string, any>>();

    // Real-time subscriptions for task updates
    const taskAssignments = useTaskAssignments(
        { tenant: 'test' },
        {
            onData: (data) => {
                console.log('New task assigned:', data);
                refetch(); // Refresh task list when new task is assigned
            }
        },
        { enabled: true, showInfo: true }
    );

    const taskStatusUpdates = useTaskStatusUpdates(
        { tenant: 'test' },
        {
            onData: (data) => {
                console.log('Task status updated:', data);
                refetch(); // Refresh task list when status changes

                // Update the specific row in Tabulator
                if (tableRef.current && data?.taskId) {
                    const row = tableRef.current.getRow(data?.taskId);
                    if (row) {
                        row.update({
                            'draft.status': data.currentStatus
                        });
                    }
                }
            }
        },
        { enabled: true, showSuccess: true, showWarnings: true }
    );

    const userTaskUpdates = useUserTaskUpdates(
        'current-user', // Auth context integration pending backend auth service
        'test',
        {
            onData: (data) => {
                console.log('User task updated:', data);
                refetch(); // Refresh when user's tasks change
            }
        }
    );

    // Initialize Tabulator with real data
    useEffect(() => {
        if (tableElementRef.current && !tableRef.current) {
            tableRef.current = new TabulatorFull(tableElementRef.current, {
                data: [],
                layout: 'fitColumns',
                movableRows: true,
                height: '400px',
                columns: [
                    { title: 'Task', field: 'draft.title', editor: 'input', minWidth: 200 },
                    {
                        title: 'Priority',
                        field: 'draft.priority',
                        editor: 'list',
                        editorParams: { values: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] },
                        formatter: (cell: { getValue: () => string }) => {
                            const value = cell.getValue();
                            const colors: Record<string, string> = {
                                LOW: 'green',
                                MEDIUM: 'blue',
                                HIGH: 'orange',
                                CRITICAL: 'red'
                            };
                            return `<span style="color: ${colors[value] || 'black'}">${value}</span>`;
                        }
                    },
                    { title: 'Assignee', field: 'draft.assignee', editor: 'input' },
                    { title: 'Due Date', field: 'draft.due_date', editor: 'input' },
                    { title: 'Status', field: 'draft.status', editor: 'list', editorParams: { values: ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED'] } },
                    { title: 'Created', field: 'created_at', formatter: 'datetime', formatterParams: { outputFormat: 'MM/DD/YYYY' } }
                ]
            });
        }
        return () => {
            if (tableRef.current) {
                tableRef.current.destroy();
                tableRef.current = null;
            }
        };
    }, []);

    // Update Tabulator data when tasks change
    useEffect(() => {
        const table = tableRef.current;
        if (!table || !tasks) return;
        // Guard against destroyed table (React StrictMode double-mount)
        try {
            if (table.element?.offsetParent !== undefined) {
                table.setData(tasks);
            }
        } catch {
            // Table may have been destroyed between render and effect
        }
    }, [tasks]);

    // Handle task creation
    const handleCreateTask = async () => {
        try {
            const taskData = {
                ...newTaskData,
                status: 'OPEN',
                task_type: 'HANDOFF',
                created_at: new Date().toISOString()
            };

            await createTask(taskData as any);
            setCreateModalOpen(false);
            setNewTaskData({
                title: '',
                priority: 'MEDIUM',
                assignee: '',
                description: '',
                due_date: ''
            });
            refetch();
        } catch (err) {
            console.error('Failed to create task:', err);
        }
    };

    // Handle task assignment
    const handleAssignTask = async (taskId: string, assignee: string) => {
        // Task assignment will be implemented when backend mutations are available
        console.log(`Assigning task ${taskId} to ${assignee}`);
        notifications.show({
            title: 'Task Assigned',
            message: `Task assigned to ${assignee}`,
            color: 'green'
        });
    };

    // Handle task approval
    const handleApproveTask = async (taskId: string) => {
        // Task approval will be implemented when backend mutations are available
        console.log(`Approving task ${taskId}`);
        notifications.show({
            title: 'Task Approved',
            message: 'Task has been approved',
            color: 'green'
        });
    };

    // Calculate statistics from real data
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'DONE').length;
    const overdueTasks = tasks.filter(t => {
        if (!t.due_at) return false;
        return new Date(t.due_at) < new Date() && t.status !== 'DONE';
    }).length;
    const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Task Management</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="tasks" leftSection={<IconChecklist size={16} />}>Tasks</Tabs.Tab>
                        <Tabs.Tab value="assignments" leftSection={<IconUsers size={16} />}>Assignments</Tabs.Tab>
                        <Tabs.Tab value="calendar" leftSection={<IconCalendar size={16} />}>Calendar</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="tasks">
                        <Stack mt="md">
                            <Group justify="space-between" mb="md">
                                <Text size="sm" c="dimmed">{loading ? 'Loading tasks...' : `${totalTasks} tasks found`}</Text>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => setCreateModalOpen(true)}
                                    disabled={creating}
                                >
                                    Create Task
                                </Button>
                            </Group>

                            <div ref={tableElementRef} />

                            {error && (
                                <Text c="red" size="sm">Error loading tasks: {error.message}</Text>
                            )}

                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" c="dimmed">Total Tasks</Text>
                                        <Title order={3}>{totalTasks}</Title>
                                        <Progress value={progressPct} mt="xs" />
                                        <Text size="xs" c="dimmed" mt="xs">{progressPct}% complete</Text>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" c="dimmed">Overdue</Text>
                                        <Title order={3} c={overdueTasks > 0 ? "red" : "green"}>{overdueTasks}</Title>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" c="dimmed">Completed</Text>
                                        <Title order={3} c="green">{completedTasks}</Title>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>

                {/* Create Task Modal */}
                <Modal
                    opened={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    title="Create New Task"
                    size="lg"
                >
                    <Stack>
                        <TextInput
                            label="Task Title"
                            placeholder="Enter task title"
                            value={newTaskData.title}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTaskData({ ...newTaskData, title: e.target.value })}
                            required
                        />

                        <TextInput
                            label="Description"
                            placeholder="Task description"
                            value={newTaskData.description}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTaskData({ ...newTaskData, description: e.target.value })}
                        />

                        <Select
                            label="Priority"
                            value={newTaskData.priority}
                            onChange={(value: string | null) => setNewTaskData({ ...newTaskData, priority: value || 'MEDIUM' })}
                            data={[
                                { value: 'LOW', label: 'Low' },
                                { value: 'MEDIUM', label: 'Medium' },
                                { value: 'HIGH', label: 'High' },
                                { value: 'CRITICAL', label: 'Critical' }
                            ]}
                        />

                        <TextInput
                            label="Assignee"
                            placeholder="Enter assignee name"
                            value={newTaskData.assignee}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTaskData({ ...newTaskData, assignee: e.target.value })}
                        />

                        <TextInput
                            label="Due Date"
                            type="date"
                            value={newTaskData.due_date}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTaskData({ ...newTaskData, due_date: e.target.value })}
                        />

                        <Group justify="flex-end" mt="md">
                            <Button
                                variant="outline"
                                onClick={() => setCreateModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateTask}
                                loading={creating}
                                disabled={!newTaskData.title}
                            >
                                Create Task
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </Paper>
        </Container>
    );
};

export default TaskEnhanced;
